/* eslint-disable react/no-array-index-key */
import clsx from "clsx";
import { mean, sum } from "lodash";

import { BreakoutRoomUsers } from "./breakout_room_users";
import { ListenButton } from "./listen_button";
import { PinButton } from "./pin_button";
import { VisitButton } from "./visit_button";

import { TableRowErrorBoundary } from "components/error_boundary";
import { useCapabilities, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { BreakoutRoomType, CapabilityType } from "shared/types";
import { UserType } from "shared/types/user";
import { WorkbookType } from "shared/types/workbook";
import { classroomSelectors, workbookSelectors } from "store";

import "./breakout_rooms_list.scss";

const AVERAGE_TALK_TIME_BUFFER = 0.2;
const BALANCED_TALK_TIME_DISTRIBUTION_COEFFICIENT = 0.15;

function BreakoutRoomListItem({
  breakoutRoom,
  breakoutRoomUsers,
  totalNumberOfBreakoutRooms,
  averageTalkTimeForAllGroups,
  showTalkTime,
  showFlavors,
  showWorkbookData,
  workbook,
  hasAv,
}: {
  breakoutRoom: BreakoutRoomType;
  breakoutRoomUsers: UserType[];
  totalNumberOfBreakoutRooms: number;
  averageTalkTimeForAllGroups: number;
  showTalkTime: boolean;
  showFlavors: boolean;
  showWorkbookData: boolean;
  workbook?: WorkbookType;
  hasAv?: boolean;
}) {
  const { canControlClassroom, canVisitBreakouts } = useCapabilities();
  const presentUsers = breakoutRoomUsers.filter((user) => user.is_present);

  // If the group's TT is no more than 20% above of the average of all group averages and also no more than 20% below,
  // the label should be "Average". Above that range, "Good"; below that range, "Quiet". If TT = 0, then it's "Silent".
  const getTalkTimeAverage = () => {
    if (presentUsers.length === 0) return null;

    const silent = <span className="talk-time--bad">Silent</span>;
    if (!breakoutRoom.talk_times) return silent;

    const talkTimeForThisGroup = sum(Object.values(breakoutRoom.talk_times));

    if (talkTimeForThisGroup === 0) {
      return silent;
    }
    if (talkTimeForThisGroup > averageTalkTimeForAllGroups * (1 + AVERAGE_TALK_TIME_BUFFER)) {
      return <span className="talk-time--high">Good</span>;
    }
    if (talkTimeForThisGroup < averageTalkTimeForAllGroups * (1 - AVERAGE_TALK_TIME_BUFFER)) {
      return <span className="talk-time--low">Quiet</span>;
    }
    return <span className="talk-time--avg">Average</span>;
  };

  // If the SD is < 15% of the TTA, label the group "Balanced". If it is > 15%, label the group "Imbalanced".
  // In either case, if there is a group member whose individual breakout talk time is > 2x the SD above the
  // group's TTA, label the group "Very Imbalanced".
  const getTalkTimeDistribution = () => {
    if (presentUsers.length === 0) return null;

    const balanced = <span className="talk-time--avg">Balanced</span>;
    if (!breakoutRoom.talk_times) return balanced;

    const talkTimes = Object.values(breakoutRoom.talk_times);
    while (talkTimes.length < presentUsers.length) {
      talkTimes.push(0);
    }
    const averageTalkTime = talkTimes.length > 0 ? mean(talkTimes) : 0;
    const squaredDiffs = talkTimes.map((talkTime) => {
      const diff = talkTime - averageTalkTime;
      return diff * diff;
    });
    const averageSquaredDiff = squaredDiffs.length > 0 ? mean(squaredDiffs) : 0;
    const standardDeviation = Math.sqrt(averageSquaredDiff);

    if (talkTimes.find((talkTime) => talkTime > 2 * standardDeviation + averageTalkTime)) {
      return <span className="talk-time--bad">Very Imbalanced</span>;
    }
    if (standardDeviation <= averageTalkTime * BALANCED_TALK_TIME_DISTRIBUTION_COEFFICIENT) {
      return balanced;
    }
    return <span className="talk-time--low">Imbalanced</span>;
  };

  const getWorkbookProgress = () => {
    if (!workbook) return "";
    const answers = workbook.answers;

    return Object.values(answers).map((answer, index) => {
      let content = Object.values(answer)[0];
      if (Array.isArray(content)) {
        // If content is an array, then the question is either multiple choice or a table
        // If the question is a table, we need to check if any cells are not null
        content = content.filter((cell) => cell !== null)[0];
      }
      const isUnitTest = typeof content === "boolean";
      const isFilled = isUnitTest || content !== undefined;
      return (
        <div
          key={index}
          className={clsx("workbook-progress-circle mr1", {
            "bg-black-tint-40": isFilled && !isUnitTest,
            "bg-green": isUnitTest && content,
            "bg-red": isUnitTest && !content,
          })}
          style={{
            borderColor:
              isUnitTest && content
                ? "var(--green)"
                : isUnitTest && !content
                  ? "var(--red)"
                  : "var(--black-tint-40)",
          }}
        />
      );
    });
  };

  return (
    <tr style={{ borderBottom: "1px solid var(--black-tint-40)" }} data-testid="group-list-item">
      <td className="px4 align-middle" style={{ width: "75px" }}>
        {breakoutRoom.title}
      </td>
      <td className="px4 align-middle" style={{ width: "100px" }}>
        {presentUsers.length} of {breakoutRoomUsers.length}
      </td>
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <td className="px2">
        <BreakoutRoomUsers
          breakoutRoom={breakoutRoom}
          users={breakoutRoomUsers}
          totalNumberOfBreakoutRooms={totalNumberOfBreakoutRooms}
          hasAv={hasAv}
          hideVideo
        />
      </td>
      {showTalkTime && (
        <>
          <td className="px4 align-middle" style={{ width: "150px" }}>
            {getTalkTimeAverage()}
          </td>
          <td className="px4 align-middle" style={{ width: "150px" }}>
            {getTalkTimeDistribution()}
          </td>
        </>
      )}
      {showFlavors && <td className="px4 align-middle">{breakoutRoom.flavor}</td>}
      {showWorkbookData && (
        <td className="px4 align-middle">
          <div className="flex">{getWorkbookProgress()}</div>
        </td>
      )}
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <td className="align-middle">
        <div className="flex items-center">
          {canVisitBreakouts && <VisitButton breakoutRoom={breakoutRoom} />}
          {hasAv && <ListenButton breakoutRoom={breakoutRoom} />}
          {canControlClassroom && <PinButton breakoutRoom={breakoutRoom} />}
        </div>
      </td>
    </tr>
  );
}

function BreakoutRoomsList({
  breakoutRooms,
  showFlavors,
  showWorkbookData,
}: {
  breakoutRooms: BreakoutRoomType[];
  showFlavors: boolean;
  showWorkbookData: boolean;
}) {
  const allUsers = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat("is_present")),
  ) as UserType[];
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const workbooks = useArraySelector(workbookSelectors.selectWorkbooksByFlavor());
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());

  let totalTalkTime = 0;
  let numberOfPresentGroups = 0;
  breakoutRooms.forEach((breakoutRoom) => {
    const someUsersArePresent = allUsers.some(
      (user) => breakoutRoom.assigned_user_ids.includes(user.id) && user.is_present,
    );
    if (someUsersArePresent) {
      numberOfPresentGroups += 1;
      if (breakoutRoom.talk_times) {
        totalTalkTime += sum(Object.values(breakoutRoom.talk_times));
      }
    }
  });
  const averageTalkTimeForAllGroups =
    numberOfPresentGroups > 0 ? totalTalkTime / numberOfPresentGroups : 0;

  const nColumns = 4 + (showFlavors ? 1 : 0) + (showWorkbookData ? 1 : 0) + (isInPerson ? 0 : 2);

  return (
    <table className="w-full">
      <thead className="bg-black-tint-20 bold left-align">
        <tr>
          <th className="p4">Group</th>
          <th className="p4">Size</th>
          <th className="p4">Participants</th>
          {!isInPerson && (
            <>
              <th className="p4">Talk Time Avg.</th>
              <th className="p4">Talk Time Dist.</th>
            </>
          )}
          {showFlavors && <th className="p4">Workbook Flavor</th>}
          {showWorkbookData && <th className="p4">Workbook Progress</th>}
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {breakoutRooms.map((breakoutRoom) => (
          <TableRowErrorBoundary
            colSpan={nColumns}
            key={breakoutRoom.id}
            text="This item cannot be displayed"
            margin="4px"
          >
            <BreakoutRoomListItem
              breakoutRoom={breakoutRoom}
              breakoutRoomUsers={allUsers.filter(
                ({ id }) =>
                  isVisibleInClassUserIds.includes(id) &&
                  (breakoutRoom.assigned_user_ids.includes(id) ||
                    breakoutRoom.visiting_user_ids.includes(id)),
              )}
              totalNumberOfBreakoutRooms={breakoutRooms.length}
              averageTalkTimeForAllGroups={averageTalkTimeForAllGroups}
              showTalkTime={!isInPerson}
              showFlavors={showFlavors}
              showWorkbookData={showWorkbookData}
              workbook={workbooks.find((workbook) => workbook.breakout_room_id === breakoutRoom.id)}
              hasAv={!isInPerson}
            />
          </TableRowErrorBoundary>
        ))}
      </tbody>
    </table>
  );
}

export { BreakoutRoomListItem, BreakoutRoomsList };
